import React, { useEffect, useState } from 'react';
import { ReactGridContainer } from '@europrocurement/flexy-components';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import type { ResizeHandleAxis } from 'react-grid-layout';
import type { DossierOpenSearchJsonldOpensearchDossierValideursStatusRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import {
    VALIDATOR_CNO,
    VALIDATOR_DEMAT_FACILE,
    VALIDATOR_IMPOTS,
    VALIDATOR_INPI,
    VALIDATOR_LEGAL_ANNOUCEMENT,
} from '@europrocurement/l2d-modules/modules/Dossiers';
import { type DashboardFiltersType } from '../../containers/FormalistDashboard/FormalistDashboard';
import ValidatorsStatusWidgetDashboard from '../ValidatorsStatusWidgetDashboard/ValidatorsStatusWidgetDashboard';

type ValidatorsStatusWidgetDashboardProps = {
    localStorageName: string;
    formalistIds: number[];
    filters: DashboardFiltersType;
    listeDossiersPath: string;
    formalistName?: string;
    showResetButton?: boolean;
};
const RESIZE_HANDLES = ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'] as ResizeHandleAxis[];

const layoutProps = [
    { i: 'greffes', x: 0, y: 0, w: 2, h: 1, isResizable: true, resizeHandles: RESIZE_HANDLES },
    {
        i: 'annonces-legales',
        x: 0,
        y: 1,
        w: 2,
        h: 1,
        isResizable: true,
        resizeHandles: RESIZE_HANDLES,
    },
    { i: 'impots', x: 0, y: 2, w: 2, h: 1, isResizable: true, resizeHandles: RESIZE_HANDLES },
    { i: 'cno', x: 0, y: 3, w: 2, h: 1, isResizable: true, resizeHandles: RESIZE_HANDLES },
];

const ValidatorsStatusWrapperWidgetDashboard: React.FunctionComponent<ValidatorsStatusWidgetDashboardProps> =
    function ({
        formalistIds,
        filters,
        listeDossiersPath,
        localStorageName,
        formalistName,
        showResetButton = false,
    }) {
        const { enqueueSnackbar } = useSnackbar();
        const { dossierOpenSearchApi } = useFormaliteService();

        const [validatorStatuses, setValidatorStatuses] = useState<
            DossierOpenSearchJsonldOpensearchDossierValideursStatusRead[]
        >([]);

        const fetchValidatorsStatus = async () => {
            if (!formalistIds) return;

            const dossierStatusResponse =
                await dossierOpenSearchApi.apiOpensearchValideursStatusGetCollection({
                    formalisteId: formalistIds,
                    itemsPerPage: 20,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                });

            if (dossierStatusResponse.status === 200) {
                setValidatorStatuses(dossierStatusResponse.data['hydra:member']);
            } else {
                enqueueSnackbar('Une erreur est survenue lors de la récupération des dossiers', {
                    variant: 'error',
                });
                setValidatorStatuses([]);
            }
        };

        useEffect(() => {
            fetchValidatorsStatus();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formalistIds, filters.startDate, filters.endDate]);

        return (
            <ReactGridContainer
                localStorageName={localStorageName}
                gridconfig={{ cols: 2, rowHeight: 200 }}
                layoutProps={layoutProps}
                showResetButton={showResetButton}
            >
                <Box key="greffes">
                    <ValidatorsStatusWidgetDashboard
                        formalistIds={formalistIds}
                        filters={filters}
                        validatorsIds={[VALIDATOR_DEMAT_FACILE, VALIDATOR_INPI]}
                        validatorLabel="Greffes"
                        listeDossiersPath={listeDossiersPath}
                        formalistName={formalistName}
                        validatorStatuses={validatorStatuses}
                    />
                </Box>
                <Box key="annonces-legales">
                    <ValidatorsStatusWidgetDashboard
                        formalistIds={formalistIds}
                        filters={filters}
                        validatorsIds={[VALIDATOR_LEGAL_ANNOUCEMENT]}
                        validatorLabel="Annonces légales"
                        listeDossiersPath={listeDossiersPath}
                        formalistName={formalistName}
                        validatorStatuses={validatorStatuses}
                    />
                </Box>
                <Box key="impots">
                    <ValidatorsStatusWidgetDashboard
                        formalistIds={formalistIds}
                        filters={filters}
                        validatorsIds={[VALIDATOR_IMPOTS]}
                        validatorLabel="Impôts"
                        listeDossiersPath={listeDossiersPath}
                        formalistName={formalistName}
                        validatorStatuses={validatorStatuses}
                    />
                </Box>
                <Box key="cno">
                    <ValidatorsStatusWidgetDashboard
                        formalistIds={formalistIds}
                        filters={filters}
                        validatorsIds={[VALIDATOR_CNO]}
                        validatorLabel="CNO"
                        listeDossiersPath={listeDossiersPath}
                        formalistName={formalistName}
                        validatorStatuses={validatorStatuses}
                    />
                </Box>
            </ReactGridContainer>
        );
    };

export default ValidatorsStatusWrapperWidgetDashboard;
